import { useState, useEffect, useCallback } from 'react'
import { getTheme, setTheme as saveTheme, getColorTheme, setColorTheme as saveColorTheme } from '../utils/storage'
import { getThemeById } from '../data/themes'
import type { Theme } from '../data/types'

function applyColorTheme(themeId: string, mode: Theme) {
  const ct = getThemeById(themeId)
  const palette = mode === 'dark' ? ct.dark : ct.light
  const root = document.documentElement

  root.style.setProperty('--orb-orange', ct.accent)
  root.style.setProperty('--orb-red', ct.accentAlt)
  root.style.setProperty('--orb-gradient-1', ct.orbGradient1)
  root.style.setProperty('--orb-gradient-2', ct.orbGradient2)
  root.style.setProperty('--orb-gradient-3', ct.orbGradient3)
  root.style.setProperty('--bg', palette.bg)
  root.style.setProperty('--bg-primary', palette.bg)
  root.style.setProperty('--bg-alt', palette.bg)
  root.style.setProperty('--bg-card', palette.bgCard)
  root.style.setProperty('--bg-card-transparent', palette.bgCard)
  root.style.setProperty('--bg-card-solid', palette.bgCardSolid)
  root.style.setProperty('--text', palette.text)
  root.style.setProperty('--text-muted', palette.textMuted)
  root.style.setProperty('--text-subtle', palette.textSubtle)
  root.style.setProperty('--border', palette.border)
  root.style.setProperty('--shadow-card', palette.shadow)
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>('light')
  const [colorTheme, setColorThemeState] = useState('purple')
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    async function load() {
      try {
        const [savedTheme, savedColor] = await Promise.all([getTheme(), getColorTheme()])
        // Use system preference if no saved theme exists (first load)
        const effectiveTheme = savedTheme || (
          window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
        )
        setThemeState(effectiveTheme)
        setColorThemeState(savedColor)
        document.documentElement.setAttribute('data-theme', effectiveTheme)
        applyColorTheme(savedColor, effectiveTheme)
      } catch (error) {
        console.error('Failed to load theme:', error)
      } finally {
        setIsLoaded(true)
      }
    }
    load()
  }, [])

  const setTheme = useCallback(async (newTheme: Theme) => {
    setThemeState(newTheme)
    document.documentElement.setAttribute('data-theme', newTheme)
    applyColorTheme(colorTheme, newTheme)
    try {
      await saveTheme(newTheme)
    } catch (error) {
      console.error('Failed to save theme:', error)
    }
  }, [colorTheme])

  const setColorTheme = useCallback(async (newColor: string) => {
    setColorThemeState(newColor)
    applyColorTheme(newColor, theme)
    try {
      await saveColorTheme(newColor)
    } catch (error) {
      console.error('Failed to save color theme:', error)
    }
  }, [theme])

  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'light' ? 'dark' : 'light'
    setTheme(newTheme)
  }, [theme, setTheme])

  return {
    theme,
    setTheme,
    toggleTheme,
    colorTheme,
    setColorTheme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
    isLoaded
  }
}
